import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './CatalogImageUploadField.css';

const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.svg'];
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

interface CatalogImageUploadFieldProps {
  label: string;
  currentImageUrl?: string;
  onUpload: (file: File) => Promise<unknown>;
  isUploading: boolean;
  disabled?: boolean;
}

export function CatalogImageUploadField({ label, currentImageUrl, onUpload, isUploading, disabled }: CatalogImageUploadFieldProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  function handleFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setError(t('admin.imageUploadInvalidFormat'));
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(t('admin.imageUploadTooLarge'));
      return;
    }

    setError(null);
    void onUpload(file);
  }

  return (
    <div className="ciu-field">
      <label className="form-label">{label}</label>
      <div className="ciu-row">
        <div className="ciu-thumb">
          {currentImageUrl
            ? <img src={currentImageUrl} alt={label} />
            : <i className="fas fa-image ciu-thumb-empty" />}
        </div>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          disabled={isUploading || disabled}
          onClick={() => inputRef.current?.click()}
        >
          {isUploading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-upload" />}
          {' '}
          {currentImageUrl ? t('admin.imageUploadChange') : t('admin.imageUploadSelect')}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          className="ciu-input-hidden"
          onChange={handleFileSelected}
        />
      </div>
      {error && <p className="ciu-error"><i className="fas fa-info-circle" /> {error}</p>}
    </div>
  );
}
